import React, {useCallback, useEffect, useState} from 'react';
import cn from 'classnames';

import LivePreviewEntry from 'coreComponents/LivePreviewEntry';
import {useLocation} from 'coreHooks';
import {useUI} from 'coreHooks';
import createCtx from 'coreUtils/createCtx';

import {ITabsLocation, ITabsProps} from './ITabs';
import {TabPane} from './Subcomponents';

import styles from './Tabs.module.scss';

export const [useTabsCtx, TabsProvider] = createCtx<{
	activeTabId: string;
	sideComponentData: Map<string, any>;
	registerSideComponentData: (tabId: string, data: any, type: string) => void;
	activeBrandCharGroup: string;
	setActiveBrandCharGroup: (char: string) => void;
}>();

const Tabs = ({children, items, classNames, sideComponent}: ITabsProps) => {
	const location = useLocation<ITabsLocation>();
	const [activeTabId, setActiveTabId] = useState('');
	const [sideComponentData, setSideComponentData] = useState<Map<string, any>>(new Map());
	const [sideComponentType, setSideComponentType] = useState(null);
	const header = useUI('header');
	const [activeBrandCharGroup, setActiveBrandCharGroup] = useState('');

	useEffect(() => {
		if (items.length && !activeTabId && location?.state?.initialTab) {
			setActiveTabId(location.state.initialTab);
		} else if (items.length && !activeTabId) {
			setActiveTabId(items[0]?.sys?.id);
		}
	}, []);

	const handleTabClick = useCallback(
		(id: string) => () => {
			setActiveTabId(() => id);
		},
		[],
	);

	/**
	 * Register side component data for each tab, where key is tab id and value is data
	 */
	const registerSideComponentData = useCallback((tabId: string, data: any, type: string) => {
		if (!sideComponentData.has(tabId)) {
			setSideComponentData((prevMap) => {
				const newData = new Map(prevMap);

				newData.set(tabId, data);

				return newData;
			});

			setSideComponentType(type);
		}
	}, []);

	const showSideComponentInBody = sideComponentType === 'alphabetic-scroll';

	return (
		<TabsProvider
			value={{
				activeTabId,
				sideComponentData,
				registerSideComponentData,
				activeBrandCharGroup,
				setActiveBrandCharGroup,
			}}
		>
			<div className={cn(styles['tabs'], 'tabs', classNames)}>
				<div className={cn(styles['tabs__heading'], classNames, {'header-hidden': !header.show})}>
					<div className={cn(styles['tabs__heading__triggers'], classNames)}>
						{items.map((item) => (
							<LivePreviewEntry key={item.sys.id} entryId={item.sys.id} type="Tab">
								<button
									type="button"
									onClick={handleTabClick(item.sys.id)}
									className={cn(styles['tabs__heading__trigger'], {
										[styles['tabs__heading__trigger--active']]: activeTabId === item.sys.id,
									})}
								>
									<span className={styles['tabs__heading__trigger-label']}>{item.title}</span>
								</button>
							</LivePreviewEntry>
						))}
					</div>

					{!!sideComponent && (
						<div
							className={cn(styles['tabs__heading__side'], classNames, {
								'show-side-component-in-body': showSideComponentInBody,
							})}
						>
							{sideComponent}
						</div>
					)}
				</div>
				<div
					className={cn(styles['tabs__body'], classNames, {
						'show-side-component-in-body': showSideComponentInBody,
					})}
				>
					{children}
					{showSideComponentInBody && !!sideComponent && (
						<div className={cn(styles['tabs__body__side-component'], classNames)}>{sideComponent}</div>
					)}
				</div>
			</div>
		</TabsProvider>
	);
};

Tabs.TabPane = TabPane;

export default Tabs;
